import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import PromptHelperIcon from '@/assets/icon-prompt-helper.svg?react';
import CustomAccordianRow from '../common/UI/CustomAccordianRow';
import axios from 'axios';
import { highlightPromptVariables } from '@/utils/prompt-helper';
import { updateMetrics } from '@/services/aiApps';
import useElysiaStore from '@/app/elysia-store';

interface PromptHelperProps {
  initiated: boolean;
  setInitiated: (boolean) => void;
}
const PromptHelper = ({ initiated, setInitiated }: PromptHelperProps) => {
  const chatSessionId = useElysiaStore((state) => state.chatSessionId);
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setIsPromptSetManually = useElysiaStore.getState().setIsPromptSetManually;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [inputPrompt, setInputPrompt] = useState('');
  const [promptBoxContent, setPromptBoxContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResponseRequested, setIsResponseRequested] = useState(false);
  const [isResponseGenerated, setIsResponseGenerated] = useState(false);
  const [reasoningResponse, setReasoningResponse] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const ws = useRef<WebSocket | null>(null);

  const handleClose = () => {
    setIsModalOpen(false);
    setInitiated(false);
    resetModal();
  };

  useEffect(() => {
    if (initiated) {
      setIsModalOpen(true);
    }
  }, [initiated]);

  const positionModal = useCallback(() => {
    const inputElement = document.getElementById('prompt-input');
    const modalElement = document.querySelector(
      '.prompt-helper-modal .modal-dialog'
    ) as HTMLElement | null;

    if (inputElement && modalElement) {
      window.requestAnimationFrame(() => {
        const inputRect = inputElement.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        if (inputRect.width === 0 || inputRect.height === 0) return;

        const modalWidth = screenWidth < 960 ? Math.max(inputRect.width, 280) : 576;

        let leftPosition =
          screenWidth < 960 ? inputRect.left : inputRect.left + (inputRect.width - modalWidth) / 2;

        leftPosition = Math.max(20, Math.min(leftPosition, screenWidth - modalWidth - 20));

        const modalHeight = modalElement.offsetHeight;

        const spaceAbove = inputRect.top;
        const spaceBelow = screenHeight - inputRect.bottom;

        let topPosition = 0;

        if (spaceAbove > modalHeight + 10) {
          topPosition = Math.max(10, inputRect.top - modalHeight - 10);
        } else if (spaceBelow > modalHeight + 10) {
          topPosition = inputRect.bottom + 10;
        } else if (spaceBelow > spaceAbove) {
          topPosition = inputRect.bottom + 10;
        } else {
          topPosition = Math.max(10, inputRect.top - modalHeight - 10);
        }
        if (screenWidth < 769) {
          // Mobile/tablet layout
          modalElement.style.position = 'fixed';
          modalElement.style.left = '0';
          modalElement.style.bottom = '0';
          modalElement.style.top = 'unset';
          modalElement.style.width = '100vw';
          modalElement.style.margin = '0';
          modalElement.style.transform = 'none';
        } else {
          modalElement.style.position = 'fixed';
          modalElement.style.left = `${leftPosition}px`;
          modalElement.style.top = `${topPosition}px`;
          modalElement.style.width = `${modalWidth}px`;
          modalElement.style.margin = '0';
          modalElement.style.transform = 'none';
          modalElement.style.bottom = 'unset';
        }
      });
    }
    // eslint-disable-next-line
  }, [reasoningResponse]);

  useEffect(() => {
    resetModal();
    setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
      el.focus();
    }, 0);
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;

    let resizeTimer: number | null = null;

    const onResize = () => {
      if (resizeTimer) {
        window.cancelAnimationFrame(resizeTimer);
      }
      resizeTimer = window.requestAnimationFrame(() => {
        positionModal();
      });
    };

    positionModal();
    const raf = window.requestAnimationFrame(() => {
      positionModal();
    });

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', positionModal);

    return () => {
      if (resizeTimer) {
        window.cancelAnimationFrame(resizeTimer);
      }
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', positionModal);
    };
  }, [isModalOpen, positionModal]);

  const handleKeyUp = () => {
    if (contentRef.current) {
      setCanSubmit(contentRef.current.innerText.trim().length > 0);
    }
  };

  useEffect(() => {
    if (isResponseRequested && inputPrompt.length) {
      ws.current = new WebSocket(
        `${import.meta.env.VITE_WS_PROTOCOL}://${import.meta.env.VITE_WS_BASE_URL}/prompt_tool/generate/stream`
      );

      ws.current.onopen = () => {
        if (ws.current) {
          const message = {
            task: inputPrompt,
            Authorization: axios.defaults.headers.common['Authorization'],
            session_id: chatSessionId,
          };
          ws.current.send(JSON.stringify(message));
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setReasoningResponse(data?.prompt_generator?.generator?.reasoning);
          if (data?.prompt_generator?.generator?.prompt) {
            const res = highlightPromptVariables(
              data?.prompt_generator?.generator?.prompt,
              '{',
              '}'
            );
            setPromptBoxContent(res);
          }
        } catch (error) {
          console.error(error);
        }
      };

      ws.current.onclose = () => {
        setIsLoading(false);
        setIsResponseGenerated(true);
      };

      ws.current.onerror = (error) => {
        console.error('error-->', error);
        handleClose();
      };

      return () => {
        if (ws.current) ws.current.close();
      };
    }
    // eslint-disable-next-line
  }, [isResponseRequested, inputPrompt]);

  const resetModal = () => {
    setPromptBoxContent('');
    setInputPrompt('');
    setReasoningResponse('');
    setIsResponseRequested(false);
    setIsResponseGenerated(false);
    setCanSubmit(false);
    setIsLoading(false);
    if (contentRef.current) {
      contentRef.current.innerText = '';
    }
  };

  const handleSubmit = () => {
    if (contentRef.current) {
      setInputPrompt(contentRef.current?.innerText as string);
      setIsLoading(true);
      setIsResponseRequested(true);
      contentRef.current.innerText = '';
    }
  };

  const handleBackClick = () => {
    setIsResponseRequested(false);
    setIsResponseGenerated(false);
    setPromptBoxContent('');
    if (contentRef.current) {
      contentRef.current.innerText = inputPrompt;
    }
  };

  const InsertPrompt = () => {
    try {
      if (contentRef.current) {
        useElysiaStore.setState({ isStreamingStopped: false });
        setIsPromptSetManually?.(true);
        setPromptInput(contentRef.current.innerText);
        updateMetrics('', chatSessionId, 'insert_prompt');
        if (contentRef.current) {
          contentRef.current.innerText = '';
        }
        handleClose();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      show={isModalOpen}
      onHide={handleClose}
      aria-label="Prompt Helper Modal"
      dialogClassName="custom-modal"
      className="prompt-helper-modal"
      backdropClassName="custom-backdrop"
      keyboard={true}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header className="custom-header">
        <div className="header-content">
          <div className="d-flex justify-center align-items-center gap-1">
            <PromptHelperIcon />
            <Modal.Title data-testid="brand-agent-header">Prompt Improver</Modal.Title>
          </div>
          <button type="button" className="btn-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="instruction-row">
          {isResponseGenerated ? (
            <div>
              <p className="m-0 p-0">
                We’ve highlighted text you can edit. If none appear, your prompt is ready to go.
              </p>
            </div>
          ) : (
            <span>
              Enter your original prompt, and we'll refine it to deliver enhanced results from
              Elysia
            </span>
          )}
          {reasoningResponse?.length > 0 && (
            <CustomAccordianRow title="Curious on how Elysia is improving your prompt?">
              {reasoningResponse}
            </CustomAccordianRow>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="content-box" id="content-box" suppressContentEditableWarning>
          {isLoading && (
            <div
              className="d-flex align-items-center gap-2 p-1"
              contentEditable="false"
              suppressContentEditableWarning
            >
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <span>Generating your prompt</span>
            </div>
          )}
          <div
            contentEditable={true}
            className="content-div"
            dangerouslySetInnerHTML={{
              __html: isResponseRequested ? promptBoxContent : inputPrompt,
            }}
            ref={contentRef}
            onKeyUp={handleKeyUp}
            suppressContentEditableWarning
          ></div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          {isResponseRequested ? (
            <Button
              data-testid="back-button"
              className="update-btn"
              onClick={handleBackClick}
              disabled={isLoading}
            >
              Back
            </Button>
          ) : (
            <Button
              data-testid="cancel-button"
              className="update-btn"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
          {isResponseRequested ? (
            <Button
              data-testid="insert-button"
              className="update-btn improve-btn"
              onClick={InsertPrompt}
              disabled={isLoading}
            >
              Insert Prompt
            </Button>
          ) : (
            <Button
              data-testid="improve-button"
              className="update-btn improve-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Improve Prompt
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PromptHelper;
